import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/employers", label: "Employers", id: "employers" },
  { to: "/candidates", label: "Candidates", id: "candidates" },
  { to: "/jobs", label: "Jobs", id: "jobs" },
  { to: "/blog", label: "Insights", id: "insights" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "border-white/[0.08] bg-[#030712]/85" : "border-transparent bg-[#030712]/40"
      }`}
    >
      <div className={`mx-auto flex max-w-7xl items-center justify-between px-6 transition-[padding] duration-500 ${scrolled ? "py-3" : "py-5"}`}>
        <Logo />
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `relative text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-white" : "text-slate-400 hover:text-slate-200"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-gradient-to-r from-sky-400 to-indigo-500 transition-[width] duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/employers"
            data-testid="nav-hire-talent-btn"
            className="group hidden items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-[13px] font-semibold text-white shadow-[0_4px_24px_rgba(37,99,235,0.35)] transition-[background-color,box-shadow] duration-300 hover:bg-blue-500 hover:shadow-[0_4px_32px_rgba(59,130,246,0.5)] sm:inline-flex"
          >
            Hire Talent
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 text-slate-200 lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/[0.06] bg-[#030712]/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-sm font-medium transition-colors ${
                      isActive ? "bg-white/[0.06] text-white" : "text-slate-400 hover:text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/employers"
                data-testid="mobile-hire-talent-btn"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-semibold text-white"
              >
                Hire Talent <ArrowRight size={14} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
